import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchVpnInfo,
  getApiErrorMessage,
  logoutVpn,
  requestVpnConfig,
  unlockVpn,
} from "../api";

const DEFAULT_INFO_ERROR = "Не удалось загрузить информацию о сервисе.";
const DEFAULT_REQUEST_ERROR = "Не удалось создать конфигурацию. Попробуйте ещё раз.";
const STATUS_REFRESH_MS = 30_000;

const STATUS_META = {
  ok: {
    code: "ONLINE",
    label: "Туннель доступен",
    className: "vpn-telemetry--ok",
    bars: 4,
  },
  degraded: {
    code: "DEGRADED",
    label: "Нестабильный сигнал",
    className: "vpn-telemetry--degraded",
    bars: 2,
  },
  paused: {
    code: "PAUSED",
    label: "Выдача выключена",
    className: "vpn-telemetry--paused",
    bars: 1,
  },
  down: {
    code: "OFFLINE",
    label: "Сервис недоступен",
    className: "vpn-telemetry--down",
    bars: 0,
  },
};

const PLATFORMS = [
  {
    id: "android",
    title: "Android",
    app: "AmneziaWG",
    hint: "Скачайте APK и установите. Затем импорт .conf или QR",
    links: [
      {
        label: "Скачать APK",
        href: "https://github.com/amnezia-vpn/amneziawg-android/releases/download/2.0.1/amneziawg-2.0.1.apk",
      },
      {
        label: "Google Play",
        href: "https://play.google.com/store/apps/details?id=org.amnezia.awg&hl=ru",
      },
    ],
  },
  {
    id: "ios",
    title: "iPhone / iPad",
    app: "AmneziaWG",
    hint: "Установите из App Store, затем импорт .conf или QR",
    links: [
      {
        label: "App Store",
        href: "https://apps.apple.com/ru/app/amneziawg/id6478942365",
      },
    ],
  },
  {
    id: "windows",
    title: "Windows",
    app: "AmneziaWG",
    hint: "Для большинства ПК: скачайте MSI и установите, затем импорт .conf",
    links: [
      {
        label: "Скачать MSI",
        href: "https://github.com/amnezia-vpn/amneziawg-windows-client/releases/download/2.0.2/amneziawg-amd64-2.0.2.msi",
      },
    ],
  },
  {
    id: "macos",
    title: "macOS",
    app: "AmneziaWG",
    hint: "То же приложение, что и для iPhone — из App Store",
    links: [
      {
        label: "App Store",
        href: "https://apps.apple.com/ru/app/amneziawg/id6478942365",
      },
    ],
  },
];

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function getRequestError(error) {
  const status = error?.response?.status;
  if (status === 429) {
    return "Сервис временно ограничил выдачу. Попробуйте позже.";
  }
  if (status === 503) {
    return "Сервис AmneziaWG временно недоступен. Попробуйте позже.";
  }
  return getApiErrorMessage(error, DEFAULT_REQUEST_ERROR);
}

function getUnlockError(error) {
  const status = error?.response?.status;
  if (status === 401) {
    return "Неверный пароль";
  }
  if (status === 429) {
    return "Слишком много попыток, попробуйте позже";
  }
  return getApiErrorMessage(error, "Не удалось войти. Попробуйте ещё раз.");
}

function isPasswordRequired(error) {
  return (
    error?.response?.status === 401 &&
    error?.response?.data?.detail === "Нужен пароль"
  );
}

function resolveStatusMeta(info) {
  if (!info) return null;
  if (info.enabled === false) {
    return STATUS_META.paused;
  }
  return STATUS_META[info.status] || STATUS_META.down;
}

function SignalBars({ active }) {
  return (
    <span className="vpn-signal" aria-hidden="true">
      {[1, 2, 3, 4].map((level) => (
        <span
          key={level}
          className={`vpn-signal-bar ${level <= active ? "is-on" : ""}`}
          style={{ "--bar-i": level }}
        />
      ))}
    </span>
  );
}

function ExternalLink({ href, children }) {
  return (
    <a
      className="vpn-link"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  );
}

export default function Vpn() {
  const [info, setInfo] = useState(null);
  const [infoLoading, setInfoLoading] = useState(true);
  const [infoError, setInfoError] = useState("");
  const [name, setName] = useState("");
  const [device, setDevice] = useState("");
  const [requestLoading, setRequestLoading] = useState(false);
  const [requestError, setRequestError] = useState("");
  const [config, setConfig] = useState(null);
  const [password, setPassword] = useState("");
  const [needsPassword, setNeedsPassword] = useState(false);
  const [unlockLoading, setUnlockLoading] = useState(false);
  const [unlockError, setUnlockError] = useState("");
  const [logoutLoading, setLogoutLoading] = useState(false);
  const resultRef = useRef(null);
  const serviceEnabled = info?.enabled !== false;
  const statusMeta = resolveStatusMeta(info);
  const showPasswordForm = info?.gate === "locked" || needsPassword;

  const loadInfo = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setInfoLoading(true);
      setInfoError("");
    }
    try {
      const data = await fetchVpnInfo();
      setInfo(data);
      if (data?.gate === "locked") {
        setConfig(null);
      }
      if (silent) setInfoError("");
    } catch (error) {
      if (!silent) {
        setInfoError(getApiErrorMessage(error, DEFAULT_INFO_ERROR));
      }
    } finally {
      if (!silent) setInfoLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInfo();
  }, [loadInfo]);

  useEffect(() => {
    if (infoError) return undefined;
    const timer = window.setInterval(() => {
      loadInfo({ silent: true });
    }, STATUS_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [infoError, loadInfo]);

  useEffect(() => {
    const node = resultRef.current;
    if (!config || !node || typeof node.scrollIntoView !== "function") return;
    node.scrollIntoView({
      behavior: prefersReducedMotion() ? "auto" : "smooth",
      block: "nearest",
    });
  }, [config]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setRequestLoading(true);
    setRequestError("");
    setConfig(null);

    try {
      const result = await requestVpnConfig({
        name: name.trim(),
        device: device.trim(),
      });
      setConfig(result);
    } catch (error) {
      if (isPasswordRequired(error)) {
        setNeedsPassword(true);
        setRequestError("");
        return;
      }
      setRequestError(getRequestError(error));
    } finally {
      setRequestLoading(false);
    }
  };

  const handleUnlock = async (event) => {
    event.preventDefault();
    setUnlockLoading(true);
    setUnlockError("");
    try {
      await unlockVpn(password);
      setPassword("");
      setNeedsPassword(false);
      await loadInfo({ silent: true });
    } catch (error) {
      setUnlockError(getUnlockError(error));
    } finally {
      setUnlockLoading(false);
    }
  };

  const handleLogout = async () => {
    setLogoutLoading(true);
    try {
      await logoutVpn();
      setConfig(null);
      await loadInfo({ silent: true });
    } catch (error) {
      setRequestError(getApiErrorMessage(error, DEFAULT_REQUEST_ERROR));
    } finally {
      setLogoutLoading(false);
    }
  };

  const submitDisabledClass = requestLoading
    ? "vpn-button--loading"
    : !serviceEnabled
      ? "vpn-button--blocked"
      : "";

  return (
    <main className="vpn-page">
      <div className="vpn-shell">
        <header className="vpn-hero vpn-enter vpn-enter--1">
          <div className="vpn-hero-copy">
            <h1>Конфигурация AmneziaWG</h1>
            <p>
              Создайте отдельную конфигурацию для одного устройства и импортируйте её
              в клиент AmneziaWG.
            </p>
          </div>

          {!infoLoading && !infoError && statusMeta && (
            <div
              className={`vpn-telemetry ${statusMeta.className}`}
              role="status"
              aria-live="polite"
              aria-label={`Статус VPN: ${statusMeta.label}`}
            >
              <div className="vpn-telemetry-scan" aria-hidden="true" />
              <div className="vpn-telemetry-main">
                <SignalBars active={statusMeta.bars} />
                <div className="vpn-telemetry-copy">
                  <div className="vpn-telemetry-row">
                    <span className="vpn-telemetry-code" translate="no">
                      {statusMeta.code}
                    </span>
                    <span className="vpn-telemetry-label">{statusMeta.label}</span>
                  </div>
                  {info?.endpoint && info.gate !== "locked" && (
                    <div className="vpn-telemetry-meta">
                      <span className="vpn-telemetry-key">endpoint</span>
                      <span className="vpn-telemetry-value" translate="no">
                        {info.endpoint}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </header>

        {infoLoading && (
          <section className="vpn-card vpn-status vpn-enter vpn-enter--2" role="status">
            Загружаем информацию о сервисе…
          </section>
        )}

        {!infoLoading && infoError && (
          <section
            className="vpn-card vpn-status vpn-status--error vpn-enter vpn-enter--2"
            role="alert"
          >
            <p>{infoError}</p>
            <button
              className="vpn-button vpn-button--secondary"
              type="button"
              onClick={() => loadInfo()}
            >
              Повторить
            </button>
          </section>
        )}

        {!infoLoading && !infoError && (
          <>
            <section
              className="vpn-apps vpn-enter vpn-enter--2"
              aria-labelledby="vpn-apps-title"
            >
              <div className="vpn-apps-heading">
                <span className="vpn-step">Шаг 1</span>
                <h2 id="vpn-apps-title">Какое приложение поставить</h2>
                <p>
                  Ставьте официальный AmneziaWG. Ссылки ведут в магазины приложений
                  или на релизы GitHub проекта Amnezia.
                </p>
              </div>

              <ul className="vpn-apps-list">
                {PLATFORMS.map((platform) => (
                  <li key={platform.id} className="vpn-apps-item">
                    <div className="vpn-apps-item-main">
                      <span className="vpn-apps-os">{platform.title}</span>
                      <span className="vpn-apps-name" translate="no">
                        {platform.app}
                      </span>
                      <span className="vpn-apps-hint">{platform.hint}</span>
                    </div>
                    <div className="vpn-apps-links">
                      {platform.links.map((link) => (
                        <ExternalLink key={link.href} href={link.href}>
                          {link.label}
                        </ExternalLink>
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            </section>

            {showPasswordForm ? (
              <section className="vpn-card vpn-enter vpn-enter--3">
                <form className="vpn-form" onSubmit={handleUnlock}>
                  <label className="vpn-field">
                    <span>Пароль</span>
                    <input
                      type="password"
                      name="vpn-gate-password"
                      autoComplete="current-password"
                      spellCheck={false}
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      disabled={unlockLoading}
                      required
                    />
                  </label>

                  {unlockError && (
                    <div className="vpn-alert" role="alert">
                      {unlockError}
                    </div>
                  )}

                  <button
                    className={`vpn-button ${unlockLoading ? "vpn-button--loading" : ""}`.trim()}
                    type="submit"
                    disabled={unlockLoading}
                  >
                    Войти
                  </button>
                </form>
              </section>
            ) : (
              <div className="vpn-grid vpn-enter vpn-enter--3">
                <section className="vpn-card">
                  <div className="vpn-card-heading">
                    <div>
                      <span className="vpn-step">Шаг 2</span>
                      <h2>Данные устройства</h2>
                    </div>
                    {info?.gate === "unlocked" && (
                      <button
                        className="vpn-button vpn-button--secondary"
                        type="button"
                        onClick={handleLogout}
                        disabled={logoutLoading}
                      >
                        Выйти
                      </button>
                    )}
                  </div>

                  <form className="vpn-form" onSubmit={handleSubmit}>
                    {!serviceEnabled && (
                      <div className="vpn-alert" role="status">
                        Выдача новых конфигураций временно приостановлена.
                      </div>
                    )}
                    <label className="vpn-field">
                      <span>Имя</span>
                      <input
                        type="text"
                        name="vpn-name"
                        autoComplete="name"
                        spellCheck={false}
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        maxLength={64}
                        placeholder="Например, Алексей…"
                        disabled={requestLoading || !serviceEnabled}
                        required
                      />
                    </label>

                    <label className="vpn-field">
                      <span>Устройство</span>
                      <input
                        type="text"
                        name="vpn-device"
                        autoComplete="off"
                        spellCheck={false}
                        value={device}
                        onChange={(event) => setDevice(event.target.value)}
                        maxLength={32}
                        placeholder="Например, телефон…"
                        disabled={requestLoading || !serviceEnabled}
                        required
                      />
                    </label>

                    <p className="vpn-hint">
                      Один конфиг — одно устройство. Для другого устройства создайте
                      новую конфигурацию.
                    </p>

                    {requestError && (
                      <div className="vpn-alert" role="alert">
                        {requestError}
                      </div>
                    )}

                    <button
                      className={`vpn-button ${submitDisabledClass}`.trim()}
                      type="submit"
                      disabled={requestLoading || !serviceEnabled}
                    >
                      {requestLoading ? "Создаём конфигурацию…" : "Получить конфигурацию"}
                    </button>
                  </form>
                </section>

                <section className="vpn-card vpn-guide">
                  <span className="vpn-step">Шаг 3</span>
                  <h2>Как подключиться</h2>
                  <ol>
                    <li>Установите приложение <strong translate="no">AmneziaWG</strong> для вашей платформы.</li>
                    <li>Получите конфигурацию на этой странице (QR или файл .conf).</li>
                    <li>В приложении нажмите «+» и отсканируйте QR или импортируйте .conf.</li>
                    <li>Включите туннель. Не используйте один конфиг на нескольких устройствах сразу.</li>
                  </ol>
                  <p className="vpn-compatibility">
                    Нужен именно клиент AmneziaWG. Обычный WireGuard и приложение AmneziaVPN
                    с этими ключами могут не подключиться.
                  </p>
                </section>
              </div>
            )}
          </>
        )}

        {config && (
          <section
            ref={resultRef}
            className="vpn-card vpn-result vpn-result--enter"
            aria-live="polite"
          >
            <div>
              <span className="vpn-step">Готово</span>
              <h2>Конфигурация создана</h2>
              <p>Отсканируйте QR-код этим устройством или скачайте файл.</p>
              <a className="vpn-button vpn-download" href={config.config_url} download>
                Скачать .conf
              </a>
            </div>
            <div className="vpn-qr">
              <img
                src={config.qr_url}
                alt="QR-код конфигурации AmneziaWG"
                width={196}
                height={196}
              />
            </div>
          </section>
        )}
      </div>
    </main>
  );
}
